
import React, { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ManageContentIdDialog } from './ManageContentIdDialog';
import { ContentIdImportDialog } from './ContentIdImportDialog';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Calendar, FileText, Search, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTableSearch } from "@/hooks/useTableSearch";

interface TopicsBusinessUnitsTableProps {
  isLoading: boolean;
  onDataChange?: () => void;
}

export function TopicsBusinessUnitsTable({ 
  isLoading, 
  onDataChange
}: TopicsBusinessUnitsTableProps) {
  const { toast } = useToast();
  const { hasFullAccess, hasBuAccess, userBu } = useAuth();
  const [contentIds, setContentIds] = useState<any[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Use the table search hook for filtering
  const {
    searchTerm,
    filteredData,
    handleSearchChange,
    clearSearch,
    isSearching
  } = useTableSearch({
    data: contentIds,
    searchableFields: ['content_id', 'description', 'tags'],
    enableInstantSearch: true
  });

  useEffect(() => {
    fetchContentIds();
  }, []);

  const fetchContentIds = async () => {
    try {
      setIsDataLoading(true);
      const { data, error } = await supabase
        .from('topics_business_units')
        .select('*')
        .not('content_id', 'is', null)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      let filteredData = data || [];
      
      // Filter by user's BU for BU Access users (app-level filtering)
      if (hasBuAccess && !hasFullAccess && userBu && Array.isArray(userBu) && userBu.length > 0) {
        // Convert userBu to string array safely
        const userBuStrings = userBu.filter((bu): bu is string => typeof bu === 'string');
        
        filteredData = filteredData.filter(item => {
          if (!item.tags || !Array.isArray(item.tags) || item.tags.length === 0) {
            return false;
          }
          // Check if the first tag (Tag 1) matches any of the user's BU
          const tag1 = item.tags[0];
          return typeof tag1 === 'string' && userBuStrings.includes(tag1);
        });
      }
      
      setContentIds(filteredData);
    } catch (error) {
      console.error("Error fetching content IDs:", error);
      toast({
        title: "Error",
        description: "Failed to load content IDs",
        variant: "destructive"
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleDeleteContentId = async (id: string, contentId: string) => {
    try {
      const { error } = await supabase
        .from('topics_business_units')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Content ID "${contentId}" deleted successfully`,
      });

      handleDataChange();
    } catch (error) {
      console.error("Error deleting content ID:", error);
      toast({
        title: "Error",
        description: "Failed to delete content ID",
        variant: "destructive"
      });
    }
  };

  const handleDataChange = () => {
    fetchContentIds();
    if (onDataChange) onDataChange();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-4">
          {/* Show Manage Content ID button for Full Access and Limited Access users (hide only from BU Access) */}
          {!hasBuAccess && (
            <ManageContentIdDialog onDataAdded={handleDataChange} />
          )}
          {hasFullAccess && (
            <ContentIdImportDialog onDataImported={handleDataChange} />
          )}
        </div>
        
        <div className="text-sm text-muted-foreground max-w-md text-right">
          <p className="font-medium text-foreground mb-1">Content ID Management Hub</p>
          <p>Organize and manage your Content IDs with advanced tagging capabilities for streamlined content operations.</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-4 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search Content IDs..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {isSearching ? (
            <span>{filteredData.length} of {contentIds.length} results</span>
          ) : (
            <span>{contentIds.length} total items</span>
          )}
        </div>
      </div>
      
      <div className="w-full">
        {isDataLoading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-6"></div>
            <p className="text-muted-foreground text-lg">Loading content IDs...</p>
          </div>
        ) : (isSearching && filteredData.length === 0) ? (
          <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/50 dark:to-gray-800/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700">
            <div className="max-w-md mx-auto">
              <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground text-xl font-medium mb-3">No Results Found</p>
              <p className="text-sm text-muted-foreground mb-6">
                No Content IDs match your search for "{searchTerm}". Try adjusting your search terms.
              </p>
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/50 dark:to-gray-800/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700">
            <div className="max-w-md mx-auto">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground text-xl font-medium mb-3">No Content IDs Found</p>
              <p className="text-sm text-muted-foreground mb-6">
                {hasBuAccess && !hasFullAccess ? 
                  "No Content IDs are available for your business unit." :
                  "Get started by creating your first Content ID to organize and track your content initiatives effectively."
                }
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {filteredData.map((item) => (
              <div key={item.id} className="group bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 overflow-hidden">
                <div className="p-4">
                  {/* Header with Content ID, Date and Delete Button */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg text-blue-600 dark:text-blue-400 truncate group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                        {item.content_id}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      {hasFullAccess && (
                        <button
                          onClick={() => handleDeleteContentId(item.id, item.content_id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                          title="Delete Content ID"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
                        {format(new Date(item.created_at), 'MMM dd')}
                      </div>
                    </div>
                  </div>
                  
                  {/* Ticket Time */}
                  {item.ticket_time && (
                    <div className="flex items-center gap-2 mb-3 text-sm text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-3 py-1.5 rounded-lg">
                      <Calendar className="h-4 w-4" />
                      <span className="font-medium">
                        {format(new Date(item.ticket_time), 'MMM dd, yyyy')}
                      </span>
                    </div>
                  )}
                  
                  {/* Description */}
                  {item.description && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  )}
                  
                  {/* Tags Section */}
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {Array.isArray(item.tags) && item.tags.length > 0 ? (
                        <>
                          {item.tags.slice(0, 4).map((tag: string, index: number) => (
                            <Badge 
                              key={index} 
                              variant="secondary" 
                              className="text-xs px-2.5 py-1 font-medium bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 border-green-200 dark:border-green-700 text-green-800 dark:text-green-300 hover:from-green-200 hover:to-emerald-200 dark:hover:from-green-900/50 dark:hover:to-emerald-900/50 transition-all"
                            >
                              {tag}
                            </Badge>
                          ))}
                          {item.tags.length > 4 && (
                            <Badge 
                              variant="outline" 
                              className="text-xs px-2.5 py-1 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-gray-600"
                            >
                              +{item.tags.length - 4} more
                            </Badge>
                          )}
                        </>
                      ) : (
                        <div className="w-full text-center py-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-lg">
                            No tags assigned
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TopicsBusinessUnitsTable;
